import { CalendarPlus, History, Hourglass, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../ui/sheet';
import { KindIcon, StageBadge, DeadlineText } from '../QualityBadges';
import { ReviewerNotes } from '../TaskCard';
import { SubmittedFeedback } from '../TaskFeedbackForm';
import { formatDate, formatDateTime, isOpen, KIND_META, REVIEW_CYCLE_LABELS, scoreTone } from '../qualityConfig';
import { ActivityTimeline } from './TaskActivity';
import { NudgeButton } from './NudgeButton';

const Stat = ({ label, children, testId }) => (
    <div className="rounded-xl border bg-muted/20 p-3" data-testid={testId}>
        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
        <div className="mt-1">{children}</div>
    </div>
);

const ItemLine = ({ item }) => (
    <li className="flex items-center gap-3 rounded-xl border px-3 py-2.5" data-testid={`detail-item-${item.item_id}`}>
        <KindIcon kind={item.kind} />
        <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{item.title}</p>
            <p className="truncate text-xs text-muted-foreground">
                {KIND_META[item.kind].short} · {item.group}{item.last_attempt_at ? ` · last try ${formatDateTime(item.last_attempt_at)}` : ''}
            </p>
        </div>
        {item.best_score !== null && item.best_score !== undefined ? (
            <div className="text-right">
                <p className={`font-heading text-lg font-bold leading-none tabular-nums ${scoreTone(item.best_score)}`}>{item.best_score}</p>
                <p className="text-[10px] text-muted-foreground">{item.attempts} {item.attempts === 1 ? 'try' : 'tries'}{item.status === 'completed' ? '' : ' · not submitted'}</p>
            </div>
        ) : <span className={`text-xs ${item.started_at ? 'font-medium text-sky-600 dark:text-sky-300' : 'text-muted-foreground'}`}>{item.started_at ? 'Started' : 'Not done'}</span>}
    </li>
);

const DeadlineHistory = ({ history }) => (
    <div>
        <p className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground"><History className="h-3 w-3" /> Deadline changes</p>
        <ol className="space-y-1.5 border-l pl-4">
            {history.map((h) => (
                <li key={h.at} className="text-xs text-muted-foreground">
                    <span className="text-foreground">{formatDate(h.from)} → {formatDate(h.to)}</span> by {h.by_name} on {formatDate(h.at)}{h.reason ? ` · “${h.reason}”` : ''}
                </li>
            ))}
        </ol>
    </div>
);

const FeedbackBlock = ({ task }) => {
    if (task.feedback) return <SubmittedFeedback feedback={task.feedback} testId="detail-feedback" />;
    const text = task.status === 'completed' ? 'Waiting for the recruiter to share whether it helped.' : 'Feedback is requested once every simulation is done.';
    return <p className="rounded-xl border border-dashed p-3 text-sm text-muted-foreground" data-testid="detail-feedback-pending">{text}</p>;
};

export const TaskDetailSheet = ({ task, currentUserId, onOpenChange, onExtend, onDelete, onNudge }) => (
    <Sheet open={Boolean(task)} onOpenChange={onOpenChange}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="task-detail-sheet">
            {task && (
                <>
                    <SheetHeader className="space-y-2 text-left">
                        <StageBadge stage={task.stage} dueSoon={task.due_soon} testId="detail-status" />
                        <SheetTitle className="font-heading text-2xl tracking-tight">{task.title}</SheetTitle>
                        <SheetDescription>{task.user_name} · {task.user_department || task.user_email}</SheetDescription>
                    </SheetHeader>
                    <div className="mt-6 space-y-6">
                        <div className="grid grid-cols-3 gap-2">
                            <Stat label="Score" testId="detail-score">
                                <p className={`font-heading text-2xl font-bold tabular-nums ${scoreTone(task.score ?? task.score_so_far)}`}>{task.score ?? task.score_so_far ?? '—'}</p>
                            </Stat>
                            <Stat label="Done"><p className="font-heading text-2xl font-bold tabular-nums">{task.completed_count}/{task.total_count}</p></Stat>
                            <Stat label="Deadline" testId="detail-deadline"><p className="text-sm font-medium">{formatDate(task.deadline)}</p><DeadlineText task={task} /></Stat>
                        </div>
                        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Hourglass className="h-3 w-3" /> {REVIEW_CYCLE_LABELS[task.review_cycle]} review · assigned by {task.assigned_by_name} on {formatDate(task.created_at)}
                        </p>
                        {task.call_feedback && <ReviewerNotes notes={task.call_feedback} />}
                        <ActivityTimeline task={task} />
                        <ul className="space-y-2">{task.items.map((item) => <ItemLine key={item.item_id} item={item} />)}</ul>
                        {task.deadline_history?.length > 0 && <DeadlineHistory history={task.deadline_history} />}
                        <FeedbackBlock task={task} />
                        {task.user_id === currentUserId ? (
                            <p className="rounded-xl border border-dashed p-3 text-sm text-muted-foreground" data-testid="detail-own-task-note">
                                This task is assigned to you, so another Quality admin has to extend, nudge or delete it. Work on it from Quality Tasks.
                            </p>
                        ) : (
                            <div className="flex flex-wrap gap-2 border-t pt-4">
                                <NudgeButton task={task} onNudge={onNudge} full />
                                {(isOpen(task) || task.status === 'missed') && (
                                    <Button onClick={() => onExtend(task)} className="bg-violet-600 text-white hover:bg-violet-700" data-testid="extend-deadline-button">
                                        <CalendarPlus className="mr-2 h-4 w-4" /> Extend deadline
                                    </Button>
                                )}
                                <Button variant="outline" onClick={() => onDelete(task)} className="text-rose-600 hover:text-rose-700" data-testid="detail-delete-button">
                                    <Trash2 className="mr-2 h-4 w-4" /> Delete task
                                </Button>
                            </div>
                        )}
                    </div>
                </>
            )}
        </SheetContent>
    </Sheet>
);
